import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { capitalize, countBy } from 'lodash';
import { Table, Badge } from '../../../ui-kit';
import { reportIcon, ReportedStatus } from '../../../../utils/incidents/decoratorTable';
import moment from 'moment';
import { selectors as incidentSelectors } from '../../../../state/modules/incidents';
import { selectors as activityReportSelectors } from '../../../../state/modules/activity-reports';
import { formatTimeHuman } from '../../../../utils/time';

import SettingsForm from '../ui-kit/SettingsForm';

export const allApprovalStatuses = ['approved', 'submitted', 'not_submitted'];

const ApprovalStatus = ({ report }) => {
  const approval_status = report.approval_status;
  if (!approval_status) return null;

  const variants = { approved: 'success', submitted: 'warning', not_submitted: 'danger' };
  const variant = variants[approval_status] || 'default';

  return (
    <Badge variant={variant}>
      {I18n.t(`classy_enum.incidents/activity_report_approval_status.${approval_status}`)}
    </Badge>
  );
};

const Settings = ({ updateWidgetSettingsField, widgetSettings }) => (
  <SettingsForm
    updateWidgetSettingsField={updateWidgetSettingsField}
    widgetSettings={widgetSettings}
    withAutoResizeHeight={true}
    withAutoScroll={true}
  >
    <SettingsForm.Section>
      <SettingsForm.Section.Header>{I18n.t('display')}</SettingsForm.Section.Header>
      <SettingsForm.Section.Content>
        <SettingsForm.RadioButtonGroup
          fieldName="types"
          options={[
            { value: 'all', name: capitalize(I18n.t('all')) },
            {
              value: 'activity_reports',
              name: I18n.t('activerecord.models.incidents/general_activity_report.other')
            },
            { value: 'incidents', name: I18n.t('activerecord.models.incidents/incident.other') }
          ]}
          labelColWidth={3}
        />
        <SettingsForm.ToggleField
          fieldName="showCount"
          label={I18n.t('dashboards.widget_archetypes.recent_activity_list.settings.show_count')}
        />
      </SettingsForm.Section.Content>
    </SettingsForm.Section>
    <SettingsForm.Section>
      <SettingsForm.Section.Header>
        {I18n.t('dashboards.widget_archetypes.recent_activity_list.settings.columns')}
      </SettingsForm.Section.Header>
      <SettingsForm.Section.Content>
        <SettingsForm.ToggleField fieldName="showIncidentTime" label={I18n.t('attributes.time')} />
        <SettingsForm.ToggleField
          fieldName="showApprovalStatus"
          label={I18n.t('activerecord.attributes.incidents/activity_report.approval_status')}
        />
        <SettingsForm.ToggleField
          fieldName="showMyAttendanceStatus"
          label={I18n.t('activerecord.attributes.incidents/activity_report.my_attendance_status')}
        />
        <SettingsForm.ToggleField
          fieldName="showActivityType"
          label={I18n.t('activerecord.attributes.incidents/activity_report.activity_types')}
        />
      </SettingsForm.Section.Content>
    </SettingsForm.Section>
    <SettingsForm.Section>
      <SettingsForm.Section.Header>
        {I18n.t('dashboards.widget_archetypes.recent_activity_list.settings.approval_status_to_show')}
      </SettingsForm.Section.Header>
      <SettingsForm.Section.Content>
        {allApprovalStatuses.map(status => (
          <SettingsForm.ToggleField
            key={status}
            fieldName={`show_${status}`}
            label={I18n.t(`classy_enum.incidents/activity_report_approval_status.${status}`)}
          />
        ))}
      </SettingsForm.Section.Content>
    </SettingsForm.Section>
  </SettingsForm>
);

const RecentActivityList = ({
  allReports,
  showIncidentTime,
  showApprovalStatus,
  showMyAttendanceStatus,
  showActivityType,
  showCount,
  types,
  show_approved,
  show_submitted,
  show_not_submitted,
  dashboardAPIManager
}) => {
  const fetchData = () => {
    // fetch incidents and activity reports created in the last 30 days
    const createdAfter = moment().subtract(30, 'days').startOf('day').toISOString();
    const params = { created_after: createdAfter };
    if (types === 'all' || types === 'incidents') {
      dashboardAPIManager.fetchIncidents({ params, forceFetch: true, replace: true });
    }
    if (types === 'all' || types === 'activity_reports') {
      dashboardAPIManager.fetchActivityReports({ params, forceFetch: true, replace: true });
    }
    dashboardAPIManager.fetchActivityTypes({ forceFetch: true });
  };

  useEffect(() => {
    fetchData(); // initial fetch
    // set up the interval to update the data every 15 minutes
    const interval = setInterval(fetchData, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const selectedColumnsHeaders = [
    showIncidentTime && I18n.t('attributes.time'),
    I18n.t('attributes.title'),
    showApprovalStatus && I18n.t('activerecord.attributes.incidents/activity_report.approval_status'),
    showMyAttendanceStatus && I18n.t('activerecord.attributes.incidents/activity_report.my_attendance_status'),
    showActivityType && I18n.t('activerecord.attributes.incidents/activity_report.activity_types')
  ].filter(Boolean);

  // which approval statuses are enabled to be shown, based on the widget settings
  const approvalStatusesToShow = [
    show_approved && 'approved',
    show_submitted && 'submitted',
    show_not_submitted && 'not_submitted'
  ].filter(Boolean);

  // filter reports based on the selected types (all, incidents or
  // activity_reports) and the selected approval statuses. Reports without an
  // approval status (e.g. incidents) are always shown
  const selectedReports = useMemo(
    () =>
      allReports.filter(
        report =>
          (types === 'all' ||
            (types === 'activity_reports' && report.type === 'activity_report') ||
            (types === 'incidents' && report.type.startsWith('incident_'))) &&
          (!report.approval_status || approvalStatusesToShow.includes(report.approval_status))
      ),
    [allReports, types, approvalStatusesToShow]
  );

  return (
    <>
      {showCount && (
        <h5 className="float-right mb-3">
          {Object.entries(countBy(selectedReports, r => r.type)).map(([type, count]) => (
            <Badge key={type} className="ms-2">
              {I18n.t(`activerecord.models.incidents/${type}.other`)}: {count}
            </Badge>
          ))}
        </h5>
      )}
      <Table>
        <thead>
          <tr>
            {selectedColumnsHeaders.map((column, index) => (
              <th key={index}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {selectedReports.map(report => (
            <tr key={report.id}>
              {showIncidentTime && <td className="text-nowrap">{formatTimeHuman(report.start_time)}</td>}
              <td>
                {reportIcon(report)}{' '}
                <a href={`/activity_reports/${report.id}`} target="_blank" rel="noreferrer">
                  {report.title}
                </a>
              </td>
              {showApprovalStatus && (
                <td>
                  <ApprovalStatus report={report} />
                </td>
              )}
              {showMyAttendanceStatus && (
                <td>
                  <ReportedStatus status={report.current_user_attendance_status} type={report.type} />
                </td>
              )}
              {showActivityType && (
                <td>
                  {report.activity_types.map(type => (
                    <Badge className="me-1" key={type.id}>
                      {type.name}
                    </Badge>
                  ))}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
};

const mapStateToProps = state => ({
  incidents: incidentSelectors.values(state),
  activityReports: activityReportSelectors.values(state),
  allReports: incidentSelectors.allReports(state)
});

const ConnectedRecentActivityList = connect(mapStateToProps)(React.memo(RecentActivityList));

Object.assign(ConnectedRecentActivityList, {
  SettingsContent: Settings,
  defaultSettings: {
    showIncidentTime: true,
    showApprovalStatus: true,
    showMyAttendanceStatus: true,
    showActivityType: true,
    types: 'all',
    show_approved: true,
    show_submitted: true,
    show_not_submitted: true
  }
});

export default ConnectedRecentActivityList;
